import { CreateExaminationReportImport, GetExaminationReportImport, } from "./examination-report-imports.js";
import { ExaminationReportImportResult } from "@pima/contracts/precautionary-index";
import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "@effect/platform";
import * as Arr from "effect/Array";
import * as ParseResult from "effect/ParseResult";
import * as S from "effect/Schema";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { PlainDateFromString } from "./temporal.js";
import { LanguageSchema } from "./column-schemas.js";
/**
 * A comma-separated query value that holds at least one item. Blank items are
 * dropped, so `a,,b` is `["a","b"]` and an empty value is rejected.
 */
const nonEmptyCsvList = (item) => S.transformOrFail(S.String, S.NonEmptyArray(item), {
    strict: true,
    decode: (value, _options, ast) => {
        const items = value
            .split(",")
            .map((part) => part.trim())
            .filter((part) => part.length > 0);
        return Arr.isNonEmptyReadonlyArray(items)
            ? ParseResult.succeed(items)
            : ParseResult.fail(new ParseResult.Type(ast, value, "expected at least one value"));
    },
    encode: (items) => ParseResult.succeed(items.join(",")),
});
const UuidCsvList = nonEmptyCsvList(S.UUID);
const StringCsvList = nonEmptyCsvList(S.String);
/**
 * `vw_precautionary_index_per_employee` row-shaped — the insights
 * precautionary-index table's one read model. `*_count` columns are
 * Postgres `bigint`, which the driver returns as a numeric string; this
 * contract keeps that string rather than risking silent precision loss.
 */
export const PrecautionaryIndexRow = S.Struct({
    user_id: S.NullOr(S.UUID),
    client_id: S.NullOr(S.UUID),
    first_name: S.NullOr(S.String),
    last_name: S.NullOr(S.String),
    email: S.NullOr(S.String),
    language: S.NullOr(LanguageSchema),
    department_id: S.NullOr(S.UUID),
    department_name: S.NullOr(S.String),
    job_title_id: S.NullOr(S.UUID),
    job_title_name: S.NullOr(S.String),
    location_ids: S.NullOr(S.String),
    location_names: S.NullOr(S.String),
    total_examinations: S.NullOr(S.String),
    completed_count: S.NullOr(S.String),
    overdue_count: S.NullOr(S.String),
    upcoming_count: S.NullOr(S.String),
    completed_abbreviations: S.NullOr(S.String),
    overdue_abbreviations: S.NullOr(S.String),
    examination_types: S.NullOr(S.String),
    earliest_due_date: S.NullOr(PlainDateFromString),
    earliest_overdue_date: S.NullOr(PlainDateFromString),
    earliest_completion_date: S.NullOr(PlainDateFromString),
    latest_due_date: S.NullOr(PlainDateFromString),
    latest_completion_date: S.NullOr(PlainDateFromString),
});
export const ListPrecautionaryIndexParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    location_ids: S.optional(UuidCsvList),
    department_ids: S.optional(UuidCsvList),
    user_ids: S.optional(UuidCsvList),
    examination_types: S.optional(StringCsvList),
    language: S.optional(LanguageSchema),
    search: S.optional(S.String),
    overdue_only: S.optionalWith(S.BooleanFromString, {
        default: () => false,
    }),
}));
export const ListPrecautionaryIndex = HttpApiEndpoint.get("listPrecautionaryIndex", "/precautionary-index")
    .setUrlParams(ListPrecautionaryIndexParams)
    .addSuccess(pagedResponse(PrecautionaryIndexRow))
    .addError(Forbidden, { status: 403 });
/** One examination entry of an employee, as their record lists it. */
export const PrecautionaryExaminationRow = S.Struct({
    id: S.UUID,
    title: S.NullOr(S.String),
    description: S.NullOr(S.String),
    abbreviation: S.NullOr(S.String),
    type: S.NullOr(S.String),
    status: S.Literal("overdue", "upcoming", "completed"),
    due_date: S.NullOr(PlainDateFromString),
    follow_up_date: S.NullOr(PlainDateFromString),
    completion_date: S.NullOr(PlainDateFromString),
    no_follow_up_required: S.NullOr(S.Boolean),
});
export const ListEmployeeExaminationsParams = S.Struct({
    client_id: S.UUID,
    user_id: S.UUID,
    language: LanguageSchema,
});
/** Every examination entry of one employee of the client, in one language. */
export const ListEmployeeExaminations = HttpApiEndpoint.get("listEmployeeExaminations", "/precautionary-index/employee-examinations")
    .setUrlParams(ListEmployeeExaminationsParams)
    .addSuccess(S.Struct({ items: S.Array(PrecautionaryExaminationRow) }))
    .addError(Forbidden, { status: 403 });
export const ExportExaminationsCsvParams = S.Struct({
    client_id: S.UUID,
    language: S.optionalWith(LanguageSchema, {
        default: () => "de-DE",
    }),
    start_date: S.optional(PlainDateFromString),
    end_date: S.optional(PlainDateFromString),
});
/** CSV bytes for a customer authorized by the typed export handler. */
export const ExportExaminationsCsv = HttpApiEndpoint.get("exportExaminationsCsv", "/precautionary-index/export")
    .setUrlParams(ExportExaminationsCsvParams)
    .addSuccess(HttpApiSchema.Uint8Array({ contentType: "text/csv; charset=utf-8" }))
    .addError(Forbidden, { status: 403 });
const ExamReportUploadEntry = S.Struct({
    first_name: S.NonEmptyString,
    last_name: S.NonEmptyString,
    date_of_birth: S.optional(S.String),
    personal_number: S.optional(S.String),
    email: S.optional(S.String),
    examination_abbreviation: S.NonEmptyString,
    examination_type: S.optional(S.String),
    assessment: S.optional(S.NullOr(S.Literal(0, 1))),
    examination_date: PlainDateFromString,
    next_examination_date: S.optional(PlainDateFromString),
});
/** Report matching and writes stay inside the explicitly authorized customer. */
export const UploadExaminationReportPayload = S.Struct({
    client_id: S.UUID,
    language: S.optionalWith(LanguageSchema, {
        default: () => "de-DE",
    }),
    entries: S.Array(ExamReportUploadEntry).pipe(S.minItems(1), S.maxItems(1000)),
});
export const UploadExaminationReportResult = ExaminationReportImportResult;
export const UploadExaminationReport = HttpApiEndpoint.post("uploadExaminationReport", "/precautionary-index/upload-report")
    .setPayload(UploadExaminationReportPayload)
    .addSuccess(S.Struct({ data: UploadExaminationReportResult }))
    .addError(Forbidden, { status: 403 });
export const precautionaryIndexGroup = HttpApiGroup.make("precautionary-index")
    .add(ListPrecautionaryIndex)
    .add(ListEmployeeExaminations)
    .add(ExportExaminationsCsv)
    .add(UploadExaminationReport)
    .add(CreateExaminationReportImport)
    .add(GetExaminationReportImport)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=precautionary-index.js.map